import {
  HTTP_INTERCEPTORS,
  HttpBackend,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  Provider,
} from '@angular/core';
import { provideRouter, Router } from '@angular/router';
import { provideStore, Store } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { MultiTranslateHttpLoader } from 'ngx-translate-multi-http-loader';
import {
  ConfigService,
  launchConfigs,
  ltiParams,
  TranslationConfigService,
} from 'up';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { LoaderInterceptor } from './shared/interceptors/loader.interceptor';
import { SetLtiParams } from './shared/store/actions/ltiparams.action';
import { appReducers } from './shared/store/reducers/app.reducers';

export function createTranslateLoader(httpBackend: HttpBackend) {
  return new MultiTranslateHttpLoader(httpBackend, [
    './assets/quid-i18n/',
    './assets/i18n/',
    './assets/up-i18n/',
  ]);
}

const interceptorConfiguration: Provider = {
  provide: HTTP_INTERCEPTORS,
  useClass: LoaderInterceptor,
  multi: true,
};

const translationLoaderConfiguration = {
  loader: {
    provide: TranslateLoader,
    useFactory: createTranslateLoader,
    deps: [HttpBackend],
  },
};

export function getAppInitializer() {
  return (): Promise<Record<string, string> | void> => {
    const launchConfigs: launchConfigs = {
      createTokenApiUrl: `${environment.apiUrl}/up/jwt/token/create`,
      paramsApiUrl: `${environment.apiUrl}/ebook/params`,
      redirectUrl: '', //'https://www.mheducation.com/highered', TBD
      environmentName: environment.envName,
    };

    const config: ConfigService = inject(ConfigService);
    const store: Store = inject(Store);
    const translationConfigService: TranslationConfigService = inject(
      TranslationConfigService,
    );
    const router: Router = inject(Router);

    return config.processRequest(launchConfigs).then(
      (ltiResponse: ltiParams) => {
        store.dispatch(new SetLtiParams({ ltiParams: ltiResponse }));
        return translationConfigService.setLocale(ltiResponse.lmsLocale);
      },
      (error) => {
        return translationConfigService.setLocale().then(() => {
          const unPairBeforeExit =
            !!error?.error?.error?.errorCode?.startsWith('FAIL_IN_PAIR');
          router.navigate(['/error'], {
            state: {
              unPairBeforeExit,
              buttonTranslationKey: unPairBeforeExit
                ? 'INSTRUCTOR.PRODUCT_SELECTION.PAIRING_ERROR_BUTTON_LABEL'
                : null,
              errorMessageLinkLabelKey: unPairBeforeExit
                ? 'INSTRUCTOR.PRODUCT_SELECTION.ERROR_LINK_LABEL'
                : null,
              translateKey: unPairBeforeExit
                ? 'INSTRUCTOR.PRODUCT_SELECTION.PAIRING_ERROR_MESSAGE'
                : null,
              isErrorWithLink: unPairBeforeExit,
            },
          });
        });
      },
    );
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    interceptorConfiguration,
    { provide: Window, useValue: window },
    provideAppInitializer(getAppInitializer()),
    provideHttpClient(withInterceptorsFromDi()),
    provideRouter(routes),
    provideStore(appReducers),
    provideStoreDevtools({
      maxAge: 25,
      logOnly: environment.production,
    }),
    provideTranslateService(translationLoaderConfiguration),
  ],
};
